import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/shared/lib/utils";
import { fetchSongsPackagesForCurrentPlatform, } from "@/shared/api/packages";
import { ymReachGoal } from "@/shared/lib/yandexMetrika";
import { getStorefrontItems } from "@/shared/lib/subscriptionView";
import { useMySubscription } from "@/features/subscription/model/useMySubscription";
import { LandingPathContext } from "./Cta";
import { bestValuePackageId, toPackageView } from "./packageView";
import { IS_PRERENDER } from "./prerender";
import landingStyles from "./Landing.module.scss";
import styles from "./Sections.module.scss";
/** Куда ведёт выбор пакета — страница пополнения, как пункт «Баланс» сайта. */
const BALANCE_PATH = "/balance";
const SKELETON_COUNT = 3;
/**
 * Реальные пакеты песен — те же данные и фильтры, что на /balance. Цены не
 * хардкодим: грузим на клиенте. Пока грузится — скелетоны той же высоты, что
 * карточки. API недоступен / пакетов нет — блок без карточек, остаются
 * заголовок, текст и CTA секции.
 *
 * Снимок пререндера цен не содержит (зависят от пользователя/AB и устареют при
 * первой смене прайса), но содержит те же скелетоны: снимок отдают всем, и
 * живое дерево начинает с «loading» — место под карточки уже занято, при
 * подмене статики ничего не сдвигается.
 *
 * Кнопка пакета — ссылка на /balance с текущим query: там AB-гейт пейвола,
 * billing-email гейт и гостевой чекаут, т.е. ровно тот путь оплаты, что у
 * пункта «Баланс». Гость туда тоже попадает (ProtectedRoute пускает гостя).
 */
export const Pricing = ({ placement }) => {
    const location = useLocation();
    const landingPath = React.useContext(LandingPathContext);
    const [state, setState] = React.useState({ status: "loading" });
    // Действующая подписка → подписочные пакеты не предлагаем (как на /balance).
    const subscriptionState = useMySubscription({ enabled: !IS_PRERENDER });
    React.useEffect(() => {
        if (IS_PRERENDER)
            return;
        let cancelled = false;
        fetchSongsPackagesForCurrentPlatform()
            .then((packages) => {
            if (cancelled)
                return;
            setState(packages.length ? { status: "ready", packages } : { status: "unavailable" });
        })
            .catch(() => {
            if (!cancelled)
                setState({ status: "unavailable" });
        });
        return () => {
            cancelled = true;
        };
    }, []);
    if (state.status === "unavailable")
        return null;
    if (state.status === "loading") {
        return (_jsx("ul", { className: styles.plans, style: { ["--cols"]: SKELETON_COUNT }, "aria-busy": "true", "aria-label": "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u043F\u0430\u043A\u0435\u0442\u044B", children: Array.from({ length: SKELETON_COUNT }, (_, i) => (_jsxs("li", { className: cn(styles.plan, styles.planSkeleton), "aria-hidden": "true", children: [_jsx("span", { className: styles.skeletonLine, style: { width: "45%" } }), _jsx("span", { className: cn(styles.skeletonLine, styles.skeletonPrice) }), _jsx("span", { className: styles.skeletonLine, style: { width: "60%" } }), _jsx("span", { className: cn(styles.skeletonLine, styles.skeletonButton) })] }, i))) }));
    }
    const storefront = getStorefrontItems(state.packages, subscriptionState);
    if (storefront.length === 0)
        return null;
    const bestId = bestValuePackageId(storefront.map((item) => item.pkg));
    return (_jsx("ul", { className: styles.plans, style: { ["--cols"]: Math.min(storefront.length, 4) }, children: storefront.map(({ pkg, state: itemState }) => {
            // Пока неизвестно, есть ли у пользователя подписка, — скелетон той же
            // коробки: у подписчика тариф не мелькнёт, у остальных вёрстка не прыгнет.
            if (itemState === "pending") {
                return (_jsxs("li", { className: cn(styles.plan, styles.planSkeleton), "aria-hidden": "true", children: [_jsx("span", { className: styles.skeletonLine, style: { width: "45%" } }), _jsx("span", { className: cn(styles.skeletonLine, styles.skeletonPrice) }), _jsx("span", { className: styles.skeletonLine, style: { width: "60%" } }), _jsx("span", { className: cn(styles.skeletonLine, styles.skeletonButton) })] }, pkg.id));
            }
            const v = toPackageView(pkg);
            const featured = v.id === bestId;
            return (_jsxs("li", { className: cn(styles.plan, featured && styles.planFeatured), children: [featured && _jsx("span", { className: styles.planBadge, children: "\u0412\u044B\u0433\u043E\u0434\u043D\u0435\u0435 \u0432\u0441\u0435\u0433\u043E" }), _jsxs("h3", { className: styles.planName, children: [v.title, v.isSubscription && _jsx("span", { className: styles.planTag, children: "\u041F\u043E\u0434\u043F\u0438\u0441\u043A\u0430" })] }), _jsxs("p", { className: styles.planPrice, children: [v.price, v.isSubscription && v.period && _jsxs("span", { children: ["/ ", v.period] })] }), v.isSubscription && (_jsxs("p", { className: styles.planText, children: [v.includes.map((line) => (_jsxs(React.Fragment, { children: [line, _jsx("br", {})] }, line))), "\u0410\u0432\u0442\u043E\u043F\u0440\u043E\u0434\u043B\u0435\u043D\u0438\u0435. \u041E\u0442\u043C\u0435\u043D\u0430 \u0432 \u043B\u044E\u0431\u043E\u0439 \u043C\u043E\u043C\u0435\u043D\u0442"] })), (v.songs || v.perSong) && (_jsxs("p", { className: styles.planText, children: [v.songs, v.songs && v.perSong && " · ", v.perSong] })), _jsx(Link, { to: { pathname: BALANCE_PATH, search: location.search }, className: cn(landingStyles.btn, landingStyles[featured ? "btn_primary" : "btn_ghost"], landingStyles.btn_md, styles.planCta), onClick: () => ymReachGoal("cta_click", {
                            placement: `landing${landingPath}:${placement}_pkg${v.id}`,
                        }), children: _jsx("span", { children: v.isSubscription ? "Оформить подписку" : "Выбрать пакет" }) })] }, v.id));
        }) }));
};
